import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { reviewApi } from '../services/api';
import { ReviewInvitationPublicDto } from '../types';

const Page = styled.main`
  min-height: 100vh;
  background: #f7fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const Panel = styled.section`
  width: min(100%, 520px);
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 1.25rem;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.08);
`;

const Header = styled.div`
  margin-bottom: 1rem;

  h1 {
    margin: 0;
    color: #1a202c;
    font-size: 1.35rem;
  }

  p {
    margin: 0.4rem 0 0;
    color: #64748b;
  }
`;

const Stars = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 0.4rem;
  margin: 1rem 0;
`;

const StarButton = styled.button<{ $active: boolean }>`
  height: 52px;
  border: 1px solid ${props => props.$active ? '#f59e0b' : '#e2e8f0'};
  background: ${props => props.$active ? '#fffbeb' : '#fff'};
  color: ${props => props.$active ? '#d97706' : '#94a3b8'};
  border-radius: 8px;
  font-size: 1.6rem;
  cursor: pointer;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 110px;
  resize: vertical;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0.75rem;
  font: inherit;
  box-sizing: border-box;
`;

const Input = styled.input`
  width: 100%;
  height: 42px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  padding: 0 0.75rem;
  font: inherit;
  box-sizing: border-box;
`;

const Field = styled.label`
  display: grid;
  gap: 0.35rem;
  color: #334155;
  font-weight: 700;
  margin-bottom: 0.85rem;

  span {
    font-size: 0.9rem;
  }
`;

const Button = styled.button`
  width: 100%;
  height: 46px;
  border: none;
  border-radius: 8px;
  background: #1a202c;
  color: white;
  font-weight: 800;
  cursor: pointer;

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

const Notice = styled.div`
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
  border-radius: 8px;
  padding: 1rem;
`;

const ReviewPage: React.FC = () => {
  const { token: pathToken = '' } = useParams<{ token: string }>();
  const [searchParams] = useSearchParams();
  const token = pathToken || searchParams.get('token') || '';
  const [invitation, setInvitation] = useState<ReviewInvitationPublicDto | null>(null);
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [customerName, setCustomerName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const response = await reviewApi.getInvitation(token);
        setInvitation(response);
        setCustomerName(response.customerName || '');
      } catch {
        toast.error('Convite de avaliação não encontrado');
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      load();
    } else {
      setLoading(false);
    }
  }, [token]);

  const submit = async () => {
    if (!rating) {
      toast.error('Escolha uma nota para continuar');
      return;
    }

    setSubmitting(true);
    try {
      await reviewApi.submit({
        token,
        rating,
        comment: comment.trim() || undefined,
        customerName: customerName.trim() || undefined,
      });
      setSubmitted(true);
      toast.success('Avaliação enviada');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Erro ao enviar avaliação');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Page><Panel><Notice>Carregando avaliação...</Notice></Panel></Page>;
  }

  if (!invitation) {
    return <Page><Panel><Notice>Convite de avaliação não encontrado.</Notice></Panel></Page>;
  }

  if (submitted || invitation.alreadyReviewed) {
    return <Page><Panel><Notice>Obrigado. Este pedido já recebeu uma avaliação.</Notice></Panel></Page>;
  }

  if (invitation.expired || invitation.status === 'EXPIRED') {
    return <Page><Panel><Notice>Este link de avaliação expirou.</Notice></Panel></Page>;
  }

  return (
    <Page>
      <Panel>
        <Header>
          <h1>Avalie seu pedido</h1>
          <p>{invitation.merchantName}{invitation.orderDisplayId ? ` · Pedido #${invitation.orderDisplayId}` : ''}</p>
        </Header>

        <Field>
          <span>Sua nota</span>
          <Stars>
            {[1, 2, 3, 4, 5].map(value => (
              <StarButton key={value} type="button" $active={value <= rating} onClick={() => setRating(value)}>
                ★
              </StarButton>
            ))}
          </Stars>
        </Field>

        <Field>
          <span>Nome</span>
          <Input value={customerName} onChange={event => setCustomerName(event.target.value)} placeholder="Como quer aparecer na avaliação" />
        </Field>

        <Field>
          <span>Comentário opcional</span>
          <TextArea value={comment} onChange={event => setComment(event.target.value)} maxLength={800} placeholder="Conte como foi sua experiência" />
        </Field>

        <Button type="button" disabled={submitting || !rating} onClick={submit}>
          Enviar avaliação
        </Button>
      </Panel>
    </Page>
  );
};

export default ReviewPage;
